import { selectShouldHideBusinessFromAiChat } from 'app-components/flux/ai-chat/aiChatSelectors';
import { getBusinessCoaIdsSet } from 'app-components/flux/categories/categoriesSelectors';
import { featureFlagsSelectors } from 'app-components/flux/feature-flags';
import * as transactionsSelectors from 'app-components/flux/transactions/transactionsSelectors';
import type { TransactionRecord } from 'app-components/flux/transactions/transactionsTypes';
import { isSplitTxn } from 'app-components/flux/transactions/transactionsUtils';
import type RootState from 'app-components/utils/redux-store/rootState';

import { isInvoiceRelatedTransaction } from '../../transactions/transactionsUtils';
import { getCoaInfo, isBusinessTransaction, isEstimatedTransaction, isInvestmentTransaction } from '../tools/utils';
import { getTransactionActionCapabilityDefinition } from './capabilities';
import { buildCleanupTransactionEligibleSnapshot } from './features/cleanup-transaction';
import { buildRecategorizeAllowedCategories, buildRecategorizeEligibleSnapshot } from './features/recategorize';
import type {
  LaunchTransactionActionArgs,
  TransactionActionBatch,
  TransactionActionCapability,
  TransactionActionEligibleTransaction,
  TransactionActionMode,
  TransactionActionSkippedTransaction,
  TransactionActionSource,
  TransactionActionUnsupportedReason,
} from './proposalTypes';
import { normalizeTransactionActionIds, resolveTransactionActionMode } from './validators';

// Framework step 2 in docs/features/transaction-actions/README.md ("Selection normalization and
// eligibility"): turns raw launch args into a TransactionActionBatch by deduping/validating
// transaction ids, gating on feature flags and phase-1 support, filtering transactions the
// capability can't act on, and building the per-transaction snapshot each capability needs.
const buildSkippedTransaction = (
  transactionId: string,
  reason: TransactionActionUnsupportedReason,
): TransactionActionSkippedTransaction => ({
  transactionId,
  reason,
});

const getUnsupportedReason = (
  state: RootState,
  transactionId: string,
  txn: TransactionRecord,
): TransactionActionSkippedTransaction | null => {
  if (isSplitTxn(txn)) {
    return buildSkippedTransaction(transactionId, 'split_transaction');
  }

  if (isInvestmentTransaction(txn)) {
    return buildSkippedTransaction(transactionId, 'investment_transaction');
  }

  if (isEstimatedTransaction(txn)) {
    return buildSkippedTransaction(transactionId, 'estimated_transaction');
  }

  if (isInvoiceRelatedTransaction(txn)) {
    return buildSkippedTransaction(transactionId, 'invoice_related_transaction');
  }

  if (selectShouldHideBusinessFromAiChat(state)) {
    const businessCoaIds = getBusinessCoaIdsSet(state);
    if (isBusinessTransaction(txn, businessCoaIds)) {
      return buildSkippedTransaction(transactionId, 'business_transaction');
    }
  }

  return null;
};

const buildEligibleTransactionSnapshot = (
  state: RootState,
  transactionId: string,
  txn: TransactionRecord,
): TransactionActionEligibleTransaction => {
  return {
    transactionId,
    payee: txn.payee ?? '',
    date: txn.postedOn,
    amount: txn.amount,
    currentCategoryName: getCoaInfo(state, txn.coa).name,
  };
};

// Generic over the snapshot shape - this is the extension point features use to build a
// richer (or narrower) per-transaction snapshot than the common TransactionActionEligibleTransaction,
// while still going through the same shared eligibility rules (see CAPABILITY_TEMPLATE.md's
// "Batch specialization" section). cleanup_transaction uses this to attach memo/tags/category id.
const buildEligibleTransactions = <T>(
  state: RootState,
  getTransaction: (transactionId: string) => TransactionRecord | undefined,
  requestedTransactionIds: string[],
  skippedTransactions: TransactionActionSkippedTransaction[],
  buildSnapshot: (state: RootState, transactionId: string, txn: TransactionRecord) => T,
): T[] =>
  requestedTransactionIds.reduce<T[]>((acc, transactionId) => {
    const txn = getTransaction(transactionId);
    if (!txn) {
      skippedTransactions.push(buildSkippedTransaction(transactionId, 'missing_transaction'));
      return acc;
    }

    const unsupported = getUnsupportedReason(state, transactionId, txn);
    if (unsupported) {
      skippedTransactions.push(unsupported);
      return acc;
    }

    acc.push(buildSnapshot(state, transactionId, txn));
    return acc;
  }, []);

// Builds the batch returned when no transaction can be eligible for the request
// (the feature is disabled, or the capability isn't supported yet). Preserves the
// capability the caller actually asked for instead of forcing 'recategorize', so
// callers and telemetry can see what was really requested.
const buildEmptyTransactionActionBatch = (
  capability: TransactionActionCapability,
  source: TransactionActionSource,
  mode: TransactionActionMode,
  requestedTransactionIds: string[],
  reason: TransactionActionUnsupportedReason,
): TransactionActionBatch => {
  const skippedTransactions = requestedTransactionIds.map((transactionId) =>
    buildSkippedTransaction(transactionId, reason),
  );

  const base = { source, mode, requestedTransactionIds, skippedTransactions };

  switch (capability) {
    case 'recategorize':
      return { ...base, capability, eligibleTransactions: [], allowedCategories: [] };
    case 'rename_payee':
      return { ...base, capability, eligibleTransactions: [] };
    case 'cleanup_transaction':
      return { ...base, capability, eligibleTransactions: [] };
    case 'create_rule':
      return { ...base, capability, eligibleTransactions: [] };
    default: {
      const exhaustiveCheck: never = capability;
      throw new Error(`Unhandled transaction action capability: ${exhaustiveCheck}`);
    }
  }
};

export const buildTransactionActionBatch = (
  state: RootState,
  args: LaunchTransactionActionArgs,
): TransactionActionBatch => {
  const requestedTransactionIds = normalizeTransactionActionIds(args.transactionIds);
  const requestedMode = resolveTransactionActionMode(args.mode, requestedTransactionIds);

  if (!featureFlagsSelectors.getFeatureFlag(state, 'aiTransactionActions')) {
    return buildEmptyTransactionActionBatch(
      args.capability,
      args.source,
      requestedMode,
      requestedTransactionIds,
      'feature_disabled',
    );
  }

  if (!getTransactionActionCapabilityDefinition(args.capability).supportedInPhase1) {
    return buildEmptyTransactionActionBatch(
      args.capability,
      args.source,
      requestedMode,
      requestedTransactionIds,
      'capability_not_supported',
    );
  }

  const transactionsById = transactionsSelectors.getResourcesById(state);
  const getTransaction = (transactionId: string): TransactionRecord | undefined =>
    args.transactionOverrides?.[transactionId] ?? transactionsById.get(transactionId);
  const skippedTransactions: TransactionActionSkippedTransaction[] = [];

  // Recompute mode from the eligible set (not the raw request) so a launch's
  // batch.mode always matches the mode actually sent to the runner, even when
  // some requested transactions get filtered out. An explicit args.mode still
  // wins, since resolveTransactionActionMode only falls back to inferring by
  // count when no mode was requested.
  const recomputeMode = (transactionIds: string[]): TransactionActionMode =>
    resolveTransactionActionMode(args.mode, transactionIds);

  if (args.capability === 'cleanup_transaction') {
    const eligibleTransactions = buildEligibleTransactions(
      state,
      getTransaction,
      requestedTransactionIds,
      skippedTransactions,
      buildCleanupTransactionEligibleSnapshot,
    );

    return {
      capability: 'cleanup_transaction',
      source: args.source,
      mode: recomputeMode(eligibleTransactions.map((transaction) => transaction.transactionId)),
      requestedTransactionIds,
      eligibleTransactions,
      skippedTransactions,
    };
  }

  if (args.capability === 'recategorize') {
    // Computed once per batch, not per transaction - every snapshot references the same array.
    const allowedCategories = buildRecategorizeAllowedCategories(state);
    const eligibleTransactions = buildEligibleTransactions(
      state,
      getTransaction,
      requestedTransactionIds,
      skippedTransactions,
      (snapshotState, transactionId, txn) =>
        buildRecategorizeEligibleSnapshot(snapshotState, transactionId, txn, allowedCategories),
    );

    return {
      capability: 'recategorize',
      source: args.source,
      mode: recomputeMode(eligibleTransactions.map((transaction) => transaction.transactionId)),
      requestedTransactionIds,
      eligibleTransactions,
      skippedTransactions,
      allowedCategories,
    };
  }

  const eligibleTransactions = buildEligibleTransactions(
    state,
    getTransaction,
    requestedTransactionIds,
    skippedTransactions,
    buildEligibleTransactionSnapshot,
  );
  const mode = recomputeMode(eligibleTransactions.map((transaction) => transaction.transactionId));

  return {
    capability: 'rename_payee',
    source: args.source,
    mode,
    requestedTransactionIds,
    eligibleTransactions,
    skippedTransactions,
  };
};
